var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FASTElement, Observable, observable, Updates } from "@microsoft/fast-element";
import { isHTMLElement, keyArrowDown, keyArrowUp, keyEnd, keyHome } from "@microsoft/fast-web-utilities";
import { MenuItem } from "../menu-item/menu-item.js";
import { MenuItemRoles } from "../menu-item/menu-item.options.js";
/**
 * MenuList
 * @summary A Menu Custom HTML Element that implements the ARIA menu pattern.
 *
 * @example
 * ```html
 * <fabric-menu-list>
 *   <fabric-menu-item>Item 1</fabric-menu-item>
 *   <fabric-menu-item>Item 2</fabric-menu-item>
 *   <fabric-menu-item>Item 3</fabric-menu-item>
 * </fabric-menu-list>
 * ```
 *
 * @attr {Element[] | undefined} menuItems - The menu items that are part of the menu list.
 * @attr {number} focusIndex - The index of the focusable element in the items array defaults to -1.
 * @attr {MenuItemRole} focusableElementRoles - Static roles that determine if an element is focusable.
 * @attr {string} role - The ARIA role for the menu list, defaults to 'menu'.
 *
 * @prop {Element[] | undefined} items - The menu items that are part of the menu list.
 *
 * @slot - The default slot for the menu items.
 *
 * @method itemsChanged - Reacts to changes in the items property. Parameters: oldValue: HTMLElement[], newValue: HTMLElement[].
 * @method focus - Focuses the first item in the menu.
 * @method handleMenuKeyDown - Handles keydown events for menu navigation.
 * @method handleFocusOut - Handles focus out events to reset menu state.
 * @method handleChange - Observes changes to the hidden attribute of child elements. Parameters: source: any, propertyName: string.
 * @method setFocus - Sets focus on a menu item based on the provided index and adjustment. Parameters: focusIndex: number, adjustment: number.
 *
 * @extends FASTElement
 * @tagname fabric-menu-list
 * @public
 */
export class MenuList extends FASTElement {
    itemsChanged(oldValue, newValue) {
        // only update children after the component is connected and
        // the setItems has run on connectedCallback
        // (menuItems is undefined until then)
        if (this.$fastController.isConnected && oldValue !== newValue) {
            this.setItems();
        }
    }
    constructor() {
        super();
        /**
         * The internal {@link https://developer.mozilla.org/docs/Web/API/ElementInternals | `ElementInternals`} instance for the component.
         *
         * @internal
         */
        this.elementInternals = this.attachInternals();
        /**
         * The index of the focusable element in the items array
         * defaults to -1
         */
        this.focusIndex = -1;
        /**
         * @internal
         */
        this.isNestedMenu = () => {
            return (this.parentElement !== null &&
                isHTMLElement(this.parentElement) &&
                this.parentElement.getAttribute("role") === "menuitem");
        };
        /**
         * if focus is moving out of the menu, reset to a stable initial state
         * @internal
         */
        this.handleFocusOut = (e) => {
            if (!this.contains(e.relatedTarget) &&
                this.menuItems !== undefined) {
                // find our first focusable element
                const focusIndex = this.menuItems.findIndex(this.isFocusableElement);
                // set the current focus index's tabindex to -1
                this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
                // set the first focusable element tabindex to 0
                this.menuItems[focusIndex].setAttribute("tabindex", "0");
                // set the focus index
                this.focusIndex = focusIndex;
            }
        };
        this.handleItemFocus = (e) => {
            const targetItem = e.target;
            if (this.menuItems !== undefined &&
                targetItem !== this.menuItems[this.focusIndex]) {
                this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
                this.focusIndex = this.menuItems.indexOf(targetItem);
                targetItem.setAttribute("tabindex", "0");
            }
        };
        /**
         * Handle change from child MenuItem element and set radio group behavior
         */
        this.changedMenuItemHandler = (e) => {
            if (this.menuItems === undefined) {
                return;
            }
            const changedMenuItem = e.target;
            const changeItemIndex = this.menuItems.indexOf(changedMenuItem);
            if (changeItemIndex === -1) {
                return;
            }
            if (changedMenuItem.role === "menuitemradio" &&
                changedMenuItem.checked === true) {
                for (let i = changeItemIndex - 1; i >= 0; --i) {
                    const item = this.menuItems[i];
                    const role = item.getAttribute("role");
                    if (role === MenuItemRoles.menuitemradio) {
                        item.checked = false;
                    }
                    if (role === "separator") {
                        break;
                    }
                }
                const maxIndex = this.menuItems.length - 1;
                for (let i = changeItemIndex + 1; i <= maxIndex; ++i) {
                    const item = this.menuItems[i];
                    const role = item.getAttribute("role");
                    if (role === MenuItemRoles.menuitemradio) {
                        item.checked = false;
                    }
                    if (role === "separator") {
                        break;
                    }
                }
            }
        };
        /**
         * check if the item is a menu item
         */
        this.isMenuItemElement = (el) => {
            return (el instanceof MenuItem ||
                (isHTMLElement(el) &&
                    el.getAttribute("role") in MenuList.focusableElementRoles));
        };
        /**
         * check if the item is focusable
         */
        this.isFocusableElement = (el) => {
            return this.isMenuItemElement(el);
        };
        this.elementInternals.role = "menu";
    }
    /**
     * @internal
     */
    connectedCallback() {
        super.connectedCallback();
        Updates.enqueue(() => {
            // wait until children have had a chance to
            // connect before setting/checking their props/attributes
            this.setItems();
        });
        this.addEventListener("change", this.changedMenuItemHandler);
    }
    /**
     * @internal
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeItemListeners();
        this.menuItems = undefined;
        this.removeEventListener("change", this.changedMenuItemHandler);
    }
    /**
     * Focuses the first item in the menu.
     *
     * @public
     */
    focus() {
        super.focus();
    }
    /**
     * @internal
     */
    handleMenuKeyDown(e) {
        if (e.defaultPrevented || this.menuItems === undefined) {
            return;
        }
        switch (e.key) {
            case keyArrowDown:
                // go forward one index
                this.setFocus(this.focusIndex + 1, 1);
                return;
            case keyArrowUp:
                // go back one index
                this.setFocus(this.focusIndex - 1, -1);
                return;
            case keyEnd:
                // set focus on last item
                this.setFocus(this.menuItems.length - 1, -1);
                return;
            case keyHome:
                // set focus on first item
                this.setFocus(0, 1);
                return;
            default:
                // if we are not handling the event, do not prevent default
                return true;
        }
    }
    removeItemListeners(items = this.items) {
        items.forEach((item) => {
            item.removeEventListener("focus", this.handleItemFocus);
            Observable.getNotifier(item).unsubscribe(this, "hidden");
        });
    }
    static elementIndent(el) {
        const role = el.getAttribute("role");
        const startSlot = el.querySelector("[slot=start]");
        if (role && role !== MenuItemRoles.menuitem) {
            return startSlot ? 2 : 1;
        }
        return startSlot ? 1 : 0;
    }
    setItems() {
        var _a;
        const children = Array.from(this.children);
        this.removeItemListeners(children);
        children.forEach((child) => Observable.getNotifier(child).subscribe(this, "hidden"));
        const newItems = children.filter((child) => !child.hasAttribute("hidden"));
        this.menuItems = newItems;
        const menuItems = this.menuItems.filter(this.isMenuItemElement);
        // if our focus index is not -1 we have items
        if (menuItems.length) {
            this.focusIndex = 0;
        }
        menuItems.forEach((item, index) => {
            item.setAttribute("tabindex", index === 0 ? "0" : "-1");
            item.addEventListener("focus", this.handleItemFocus);
        });
        /**
         * Set the indent attribute on MenuItem elements based on their
         * position in the MenuList. Each MenuItem element has a data-indent attribute that is
         * used to set the indent of the element's start slot content.
         */
        const filteredMenuListItems = (_a = this.menuItems) === null || _a === void 0 ? void 0 : _a.filter(this.isMenuItemElement);
        const indent = filteredMenuListItems === null || filteredMenuListItems === void 0 ? void 0 : filteredMenuListItems.reduce((accum, current) => {
            const elementValue = MenuList.elementIndent(current);
            return Math.max(accum, elementValue);
        }, 0);
        filteredMenuListItems === null || filteredMenuListItems === void 0 ? void 0 : filteredMenuListItems.forEach((item) => {
            if (item instanceof MenuItem) {
                item.setAttribute("data-indent", `${indent}`);
            }
        });
    }
    /**
     * Method for Observable changes to the hidden attribute of child elements
     */
    handleChange(source, propertyName) {
        if (propertyName === "hidden") {
            this.setItems();
        }
    }
    setFocus(focusIndex, adjustment) {
        if (this.menuItems === undefined) {
            return;
        }
        while (focusIndex >= 0 && focusIndex < this.menuItems.length) {
            const child = this.menuItems[focusIndex];
            if (this.isFocusableElement(child)) {
                // change the previous index to -1
                if (this.focusIndex > -1 &&
                    this.menuItems.length >= this.focusIndex - 1) {
                    this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
                }
                // update the focus index
                this.focusIndex = focusIndex;
                // update the tabindex of next focusable element
                child.setAttribute("tabindex", "0");
                // focus the element
                child.focus();
                break;
            }
            focusIndex += adjustment;
        }
    }
}
MenuList.focusableElementRoles = MenuItemRoles;
__decorate([
    observable
], MenuList.prototype, "items", void 0);
//# sourceMappingURL=menu-list.js.map